import { useMemo } from 'react'

import { Header } from 'components'
import { Activity, type ActivityRow } from 'components/display/Activity/Activity'
import { toCertificateActivityRow } from 'components/display/Activity/certificateActivityRows'
import { useAuth } from 'hooks'
import { useCompanyCertificateActivityInfinite } from 'hooks/api'

/**
 * Certificate activity — the full-page, company-wide view of the same feed Home's
 * "Recent activity" card shows a fixed 6-row slice of (VRG-430, VRG-338).
 *
 * Link target for that card's "View all" button. Shares its data source
 * (`companyCertificateActivityApi` / GET /action-log/company) and row mapping
 * (`toCertificateActivityRow`) with `RecentActivity` — same feed, no `limit`,
 * infinite-scrolled instead of a fixed slice.
 *
 * NOT the audit log (Settings → Activity Log / `ActivityLogPage`): this records what
 * happened to the PRODUCTS (scans, transfers, issuance), including customers and
 * anonymous visitors who never appear in that log. See
 * `companyCertificateActivityApi`'s docblock for the full distinction.
 */
export const CertificateActivityPage = () => {
  const { profile } = useAuth()

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCompanyCertificateActivityInfinite()

  const rows: ActivityRow[] = useMemo(
    () =>
      (data?.pages ?? [])
        .flatMap((p) => p.data)
        .map((item) => toCertificateActivityRow(item, profile?.id)),
    [data, profile?.id]
  )

  return (
    <>
      <Header
        title="Certificate activity"
        breadcrumbsOveride="Certificate activity"
        rightComponents={null}
      />
      <Activity
        activity={rows}
        loading={isLoading}
        hasMore={!!hasNextPage}
        loadingMore={isFetchingNextPage}
        onLoadMore={fetchNextPage}
        fillHeight
        actor="name"
        emptyTitle="No certificate activity"
        emptyText="Scans, transfers and new certificates will appear here."
      />
    </>
  )
}
